<template>
  <BaseTabs :model-value="currentTab">
    <BaseTab
      v-for="tab in visibleTabs"
      :key="tab.name"
      :data-testid="`organisation-tab-${tab.name}`"
      :data-walkthrough-id="`organisation-tab-${tab.name}`"
      :label="tab.label"
      :name="tab.name"
      :to="tab.to"
      @click="tabClicked(tab)"
    />
  </BaseTabs>
</template>

<script>
import { storeToRefs } from "pinia";

import { BaseTab, BaseTabs } from "shared/components/base";
import useProductMenu from "shared/composables/useProductMenu";
import { useUserStore } from "shared/stores/user";

import { BRAND_PROFILES_ROUTES, PRODUCT_TABS } from "./constants";

export default {
  name: "OrganisationTabs",
  components: {
    BaseTab,
    BaseTabs,
  },
  setup() {
    const { currentRouteProductMenu } = useProductMenu();
    const userStore = useUserStore();

    const { organisation, role } = storeToRefs(userStore);

    const { isSuperRole } = userStore;

    return {
      currentRouteProductMenu,
      isSuperRole,
      organisation,
      role,
    };
  },
  computed: {
    hasTeamWorkspaces() {
      return this.$features.has("has_team_workspaces");
    },
    showStreamsTab() {
      return (
        this.hasTeamWorkspaces && this.isSuperRole(this.currentRouteProductMenu)
      );
    },
    currentTab() {
      if (BRAND_PROFILES_ROUTES.includes(this.$route.name)) {
        return "brand-profiles";
      }

      const currentTab = this.visibleTabs.find(
        (tab) => tab.name === this.$route.name
      );

      if (currentTab) return currentTab.name;

      return this.visibleTabs[0]?.name;
    },
    visibleTabs() {
      const tabs = [];

      this.tabs.forEach((tab) => {
        if (tab.hasPermission && this.hasAccess(tab.name)) {
          tabs.push({ name: tab.name, label: tab.label, to: tab.to });
        }
      });

      return tabs;
    },
    usersOrTeamTab() {
      if (this.showUsers()) {
        return {
          name: "users",
          label: this.$t("routes.users"),
          to: { name: "users" },
          hasPermission: true,
        };
      }

      return {
        name: "team",
        label: this.$t("routes.team"),
        to: { name: "team" },
        hasPermission: this.showTeam(),
      };
    },
    streamsTab() {
      if (
        this.currentRouteProductMenu === "media" ||
        this.currentRouteProductMenu === "dashboard"
      ) {
        return {
          name: "mention-streams",
          label: this.$t("routes.mention_streams"),
          to: { name: "mention-streams" },
          hasPermission: this.showStreamsTab,
        };
      }

      return {
        name: "social-streams",
        label: this.$t("routes.social_streams"),
        to: { name: "social-streams" },
        hasPermission: this.showStreamsTab,
      };
    },
    tabs() {
      return [
        {
          name: "usage",
          label: this.$t("routes.usage"),
          to: {
            name: "usage",
          },
          hasPermission: this.showUsage(),
        },
        {
          name: "ratecard",
          label: this.$t("routes.rate_card"),
          to: {
            name: "ratecard",
          },
          hasPermission: this.showRateCard(),
        },
        this.usersOrTeamTab,
        {
          name: "clients",
          label: this.$t("routes.clients"),
          to: {
            name: "clients",
          },
          hasPermission: this.showClients(),
        },
        {
          name: "teams",
          label: this.$t("routes.teams"),
          to: { name: "teams" },
          hasPermission: this.showOrganisationTeams(),
        },
        this.streamsTab,
        {
          name: "brand-profiles",
          label: this.$t("routes.brand_profiles"),
          to: {
            name: "brand-profiles",
            params: {
              type: "reporting",
            },
          },
          hasPermission: this.showBrandProfiles(),
        },
        {
          name: "distribution-list",
          label: this.$t("routes.distribution_lists"),
          to: {
            name: "distribution-list",
          },
          hasPermission: this.showDistributionList(),
        },
        {
          name: "integrations",
          label: this.$t("routes.integrations"),
          to: {
            name: "integrations",
          },
          hasPermission: true,
        },
        {
          name: "metric-editor",
          label: this.$t("routes.metric_editor"),
          to: {
            name: "metric-editor",
          },
          hasPermission: this.showMetricEditor(),
        },
        {
          name: "domains",
          label: this.$t("routes.domains"),
          to: {
            name: "domains",
          },
          hasPermission: this.showDomains(),
        },
        {
          name: "filter-sets",
          label: this.$t("routes.filter_sets"),
          to: {
            name: "filter-sets",
          },
          hasPermission: true,
        },
        {
          name: "query-snippets",
          label: this.$t("routes.query_snippets"),
          to: {
            name: "query-snippets",
          },
          hasPermission: true,
        },
      ];
    },
  },
  mounted() {
    if (this.$route.name !== "organisation") return;

    let initialTab = this.visibleTabs.find(
      (tab) => tab.name === this.currentTab
    );

    initialTab ??= this.visibleTabs[0];

    this.$safeRouterReplace(initialTab.to);
  },
  methods: {
    tabClicked(tab) {
      this.$track(`Clicked to view ${tab.label} in Organisation page`);
    },
    showUsers() {
      return (
        this.$organisationFeatures.canManageUsers() &&
        this.$features.has("has_user_self_management")
      );
    },
    showOrganisationTeams() {
      return (
        this.$features.has("has_team_workspaces") &&
        this.isSuperRole(this.currentRouteProductMenu)
      );
    },
    showUsage() {
      if (this.hasTeamWorkspaces) {
        return this.isSuperRole(this.currentRouteProductMenu);
      }

      return this.$organisationFeatures.canViewOrganisationUsage();
    },
    showRateCard() {
      return this.$organisationFeatures.canViewRateCard();
    },
    showBrandProfiles() {
      return this.$organisationFeatures.canManageBrandProfiles();
    },
    showClients() {
      return this.organisation && this.organisation.pr_agency;
    },
    showTeam() {
      return this.$organisationFeatures.canManageUsers();
    },
    showDistributionList() {
      return (
        this.isSuperRole(this.currentRouteProductMenu) &&
        this.$organisationFeatures.canManageDistributionLists()
      );
    },
    showMetricEditor() {
      return this.role === "super";
    },
    showDomains() {
      return (
        this.isSuperRole(this.currentRouteProductMenu) &&
        this.$features.has("has_domain_management")
      );
    },
    hasAccess(tabName) {
      if (!PRODUCT_TABS[this.currentRouteProductMenu]) return true;

      return PRODUCT_TABS[this.currentRouteProductMenu].includes(tabName);
    },
  },
};
</script>
